import { pxToFractions, fractionsToPx, ParsedValue } from './units.js';
import { ResizeHandles, PanelChildren } from '../types/shared-panel-types.js';

/**
 * If any of the panels reached its minWidth while resizing the window, we need to re-calculate
 * the sizes since the panels reaching minWidth should consume more fractions.
 */
export function redistributeFractions(
  panels: PanelChildren,
  resizeHandles: ResizeHandles,
  sizes: Record<string, ParsedValue>,
  rootWidth: number,
) {
  const newSizes = { ...sizes };
  const panelsAtMinWidth: string[] = [];
  // Total amount of fractions that panels need to shrink to make space for the panels at minWidth.
  let delta = 0;

  // Check if any of the panels is at its minWidth. If so, we need to increase its size.
  for (const panel of panels) {
    const currentSizePx = fractionsToPx(
      sizes[panel.name].value,
      rootWidth,
      resizeHandles.length,
    );

    if (
      panel.minWidth &&
      currentSizePx < panel.minWidth &&
      currentSizePx !== 0
    ) {
      panelsAtMinWidth.push(panel.name);
      const targetFractions = pxToFractions(
        panel.minWidth,
        rootWidth,
        resizeHandles.length,
      );
      // The difference between minWidth in fractions and the previous size
      // needs to be subtracted from other panels.
      delta += targetFractions - sizes[panel.name].value;
      newSizes[panel.name].value = targetFractions;
    }
  }

  // No panel is at its minWidth yet, leave everything as is.
  if (delta) {
    // To preserve the px size of all panels, the delta needs to be subtracted
    // considering the percentage of space the panels to subtract from take up.

    // The total amount of fractions of all the panels we can shrink.
    const totalFractions = panels.reduce((fr, panel) => {
      if (!panelsAtMinWidth.includes(panel.name)) {
        return fr + sizes[panel.name].value;
      }

      return fr;
    }, 0);

    for (const panel of panels) {
      if (!panelsAtMinWidth.includes(panel.name)) {
        const offsetPercentage =
          (100 / totalFractions) * sizes[panel.name].value;
        newSizes[panel.name].value -=
          Math.round(delta * offsetPercentage) / 100;
      }
    }
  }

  return newSizes;
}
